import { useMutation } from "@apollo/client/react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { PasswordCheckers } from "../../../components/PasswordCheckers/PasswordCheckers";
import PasswordTextField from "../../../components/PasswordTextField/PasswordTextField";
import { StatefulButton } from "../../../components/ui/muiButtons";
import type { SaveUserMutation, SaveUserMutationVariables } from "../../../graphql/types";
import { SaveUserDocument } from "../../../graphql/types";
import { VALID_SPECIAL_CHARS_REGEX, validatePasswordPolicy } from "../../../lib/Util";

const ChangePassword = ({ userEmail, closeModal }: { userEmail: string; closeModal: () => void }) => {
  const [saveUser, saveUserState] = useMutation<SaveUserMutation, SaveUserMutationVariables>(SaveUserDocument, {});
  const {
    control,
    formState: { isValid },
    reset,
    handleSubmit,
    watch,
    trigger,
    getValues,
    setError,
  } = useForm<{
    password: string;
    confirmPassword: string;
  }>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });
  const onSubmit = (password: string) => {
    saveUser({
      variables: {
        email: userEmail,
        userPassword: password,
      },
      onCompleted: (result) => {
        if (result.saveUserV2.__typename === "SaveUserSuccessMutationResult") {
          toast.success("Password updated");
          closeModal();
          reset();
        } else if (result.saveUserV2.__typename === "SaveUserErrors") {
          result.saveUserV2.errors?.some((error) => {
            if (error.__typename === "InsecurePasswordError") {
              setError("password", {
                message: error.passwordErrors?.join(", "),
              });
            }
          });
        }
      },
      onError: (err) => {
        console.error(err);
        toast.error("Error when updating password");
      },
    });
  };

  // eslint-disable-next-line react-hooks/incompatible-library
  const watchedPassword = watch("password");
  const validateConfirmPassword = (value: string) => {
    if (value !== getValues("password")) {
      return "Passwords do not match";
    }
  };

  return (
    <Dialog
      open={true}
      onClose={() => {
        closeModal();
        reset();
      }}
      fullWidth
      maxWidth="sm"
    >
      <Alert severity="warning" sx={{ width: "100%" }}>
        Changing the password will log out the user from all current sessions.
      </Alert>
      <form onSubmit={handleSubmit((data) => onSubmit(data.password))}>
        <DialogTitle variant="heading.four">Change user password</DialogTitle>
        <DialogContent>
          <Grid container sx={{ justifyContent: "center" }}>
            <Controller
              name="password"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  const res = validatePasswordPolicy(value);
                  if (res.errors) {
                    return res.errors;
                  }
                },
              }}
              render={({ field, fieldState }) => (
                <PasswordTextField
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    trigger("confirmPassword");
                  }}
                  margin="normal"
                  label="Password"
                  fullWidth
                  error={Boolean(fieldState.error)}
                  required
                  id="password"
                  autoComplete="off"
                  helperText={
                    fieldState.error?.message?.includes("No special characters from")
                      ? `There is an invalid special character, choose from ${VALID_SPECIAL_CHARS_REGEX.source}`
                      : ""
                  }
                  data-testid="password"
                />
              )}
            />
          </Grid>
          <PasswordCheckers password={watchedPassword} fixedWidth />
          <Grid container sx={{ justifyContent: "center" }}>
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: true,
                validate: validateConfirmPassword,
              }}
              render={({ field, fieldState }) => (
                <PasswordTextField
                  {...field}
                  margin="normal"
                  label="Confirm password"
                  fullWidth
                  error={Boolean(fieldState.error)}
                  required
                  id="confirmPassword"
                  helperText={fieldState.error?.message}
                  autoComplete="off"
                  data-testid="confirmPassword"
                />
              )}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeModal()} variant="outlined">
            Cancel
          </Button>
          <StatefulButton type="submit" loading={saveUserState.loading} disabled={!isValid}>
            Confirm
          </StatefulButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export { ChangePassword };
